import { lazy, Suspense, useState, memo } from "react";
import LazySection from "@/components/LazySection";
import { Link } from "react-router-dom";

import HeroBanner from "@/components/HeroBanner";
import ProductCard from "@/components/ProductCard";
import DynamicBanner from "@/components/DynamicBanner";
import { useSiteTheme } from "@/hooks/useSiteTheme";
import { useSiteContent } from "@/hooks/useSiteContent";
import { useDesignProducts } from "@/hooks/useDesignProducts";

const Categories = lazy(() => import("@/components/Categories"));
const FeaturesBanner = lazy(() => import("@/components/FeaturesBanner"));

const ProductGridSkeleton = ({ count = 4, cols = "grid-cols-2 md:grid-cols-3" }: { count?: number; cols?: string }) => {
  const { theme } = useSiteTheme();
  return (
    <div className={`grid ${cols} gap-4 md:gap-8`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="space-y-3">
          <div className="rounded-lg overflow-hidden animate-pulse" style={{ aspectRatio: "3/4", backgroundColor: `hsl(${theme.cardText} / 0.18)` }} />
          <div className="space-y-2 px-1">
            <div className="h-4 w-3/4 rounded animate-pulse" style={{ backgroundColor: `hsl(${theme.cardText} / 0.15)` }} />
            <div className="h-4 w-1/2 rounded animate-pulse" style={{ backgroundColor: `hsl(${theme.cardText} / 0.12)` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const SectionHeaderSkeleton = () => {
  const { theme } = useSiteTheme();
  return (
    <div className="text-center mb-8 md:mb-14">
      <div className="h-3 w-24 mx-auto mb-3 rounded animate-pulse" style={{ backgroundColor: `hsl(${theme.mutedText} / 0.3)` }} />
      <div className="h-8 w-48 mx-auto rounded animate-pulse" style={{ backgroundColor: `hsl(${theme.cardText} / 0.2)` }} />
    </div>
  );
};

const BestSellingSection = memo(({ enabled }: { enabled: boolean }) => {
  const { theme } = useSiteTheme();
  const { bestProducts } = useDesignProducts({
    designId: "1",
    bestLimit: 6,
    enabled,
  });

  if (!enabled || bestProducts.length === 0) {
    return (
      <section className="py-10 md:py-20" style={{ backgroundColor: `hsl(${theme.sectionAltBg})`, minHeight: "600px" }}>
        <div className="container mx-auto px-4" style={{ maxWidth: 1440 }}>
          <SectionHeaderSkeleton />
          <ProductGridSkeleton count={6} />
        </div>
      </section>
    );
  }

  return (
    <section data-section-id="new_arrivals" className="py-10 md:py-20" style={{ backgroundColor: `hsl(${theme.sectionAltBg})` }}>
      <div data-section-id="marquee" className="container mx-auto px-4" style={{ maxWidth: 1440 }}>
        <header className="flex items-end justify-between mb-8 md:mb-14 border-b pb-4" style={{ borderColor: `hsl(${theme.borderColor} / 0.3)` }}>
          <div>
            <p className="text-[10px] md:text-xs tracking-[0.4em] uppercase mb-2" style={{ color: `hsl(${theme.cardAccent})`, fontFamily: "var(--design-font-ui)", fontWeight: 600 }}>
              Editor's Pick
            </p>
            <h2 className="text-2xl md:text-4xl" style={{ color: `hsl(${theme.cardText})`, fontFamily: "var(--design-font-heading)", fontWeight: 700 }}>
              Best Selling
            </h2>
          </div>
          <Link to="/best-selling" className="text-xs tracking-wider uppercase flex items-center gap-1.5 pb-1 transition-opacity duration-300 hover:opacity-70" style={{ color: `hsl(${theme.cardAccent})`, fontFamily: "var(--design-font-ui)", fontWeight: 700 }}>
            See All <span aria-hidden="true">→</span>
          </Link>
        </header>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
          {bestProducts.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </div>
    </section>
  );
});
BestSellingSection.displayName = "BestSellingSection";

const AllCollectionsSection = memo(({ enabled }: { enabled: boolean }) => {
  const { theme } = useSiteTheme();
  const { allProducts } = useDesignProducts({
    designId: "1",
    allLimit: 8,
    enabled,
  });

  if (!enabled || allProducts.length === 0) {
    return (
      <section className="py-10 md:py-20" style={{ backgroundColor: `hsl(${theme.sectionBg})`, minHeight: "700px" }}>
        <div className="container mx-auto px-4" style={{ maxWidth: 1440 }}>
          <SectionHeaderSkeleton />
          <ProductGridSkeleton count={6} cols="grid-cols-2 md:grid-cols-3" />
        </div>
      </section>
    );
  }

  return (
    <section data-section-id="all_collections" className="py-10 md:py-20" style={{ backgroundColor: `hsl(${theme.sectionBg})` }}>
      <div className="container mx-auto px-4" style={{ maxWidth: 1440 }}>
        <header className="text-center mb-8 md:mb-14">
          <h2 className="text-2xl md:text-4xl" style={{ color: `hsl(${theme.cardText})`, fontFamily: "var(--design-font-heading)", fontWeight: 700 }}>
            All Collections
          </h2>
          <p className="text-xs md:text-sm mt-3 max-w-md mx-auto" style={{ color: `hsl(${theme.mutedText})`, fontFamily: "var(--design-font-body)" }}>
            Explore our complete range
          </p>
        </header>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
          {allProducts.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
        <div className="text-center mt-8 md:mt-12">
          <Link to="/products" className="inline-flex items-center gap-2 px-8 py-3 text-xs tracking-[0.3em] uppercase border transition-all duration-300 hover:scale-105" style={{ borderColor: `hsl(${theme.cardAccent})`, color: `hsl(${theme.cardText})`, borderRadius: theme.btnRadius, fontFamily: "var(--design-font-ui)", fontWeight: 700 }}>
            Browse All <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
});
AllCollectionsSection.displayName = "AllCollectionsSection";

const Design1 = () => {
  const { theme } = useSiteTheme();
  const { t: siteT } = useSiteContent();
  const [categoriesVisible, setCategoriesVisible] = useState(false);
  const [bestVisible, setBestVisible] = useState(false);
  const [allVisible, setAllVisible] = useState(false);
  const [newVisible, setNewVisible] = useState(false);

  const { newProducts } = useDesignProducts({
    designId: "1",
    newLimit: 4,
    enabled: newVisible,
  });

  const goldGrad = `linear-gradient(90deg, transparent, hsl(${theme.cardAccent}), transparent)`;

  return (
    <>
      <HeroBanner />

      <LazySection height="420px" rootMargin="160px" onVisible={() => setCategoriesVisible(true)}>
        <Suspense fallback={
          <section className="py-10 md:py-20" style={{ backgroundColor: `hsl(${theme.sectionBg})`, minHeight: "420px" }}>
            <div className="container mx-auto px-4">
              <SectionHeaderSkeleton />
              <div className="flex justify-center gap-6 flex-wrap">
                {[1,2,3,4,5,6].map(i => (
                  <div key={i} className="w-20 h-20 md:w-28 md:h-28 rounded-full animate-pulse" style={{ backgroundColor: `hsl(${theme.cardText} / 0.18)` }} />
                ))}
              </div>
            </div>
          </section>
        }>
          <Categories enabled={categoriesVisible} />
        </Suspense>
      </LazySection>

      <LazySection height="600px" rootMargin="0px" onVisible={() => setNewVisible(true)}>
        <section className="py-10 md:py-20" style={{ backgroundColor: `hsl(${theme.sectionBg})`, minHeight: "600px", contain: "layout style" }}>
          {newProducts.length > 0 ? (
            <div className="container mx-auto px-4" style={{ maxWidth: 1440 }}>
              <header className="text-center mb-8 md:mb-14">
                <p className="text-[10px] md:text-xs tracking-[0.5em] uppercase mb-3" style={{ color: `hsl(${theme.mutedText})`, fontFamily: "var(--design-font-ui)", fontWeight: 500 }}>
                  Just Arrived
                </p>
                <h2 className="text-2xl md:text-4xl lg:text-5xl" style={{ color: `hsl(${theme.cardText})`, fontFamily: "var(--design-font-heading)", fontWeight: 700 }}>
                  New Arrivals
                </h2>
                <div className="w-20 h-[2px] mx-auto mt-4 rounded-full" style={{ background: goldGrad }} />
              </header>
              <div className="grid grid-cols-2 gap-3 md:hidden">
                {newProducts.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
              <div className="hidden md:grid md:grid-cols-4 gap-5">
                <div className="col-span-3 grid grid-cols-3 gap-5">
                  {newProducts.slice(0, 3).map((p) => <ProductCard key={p.id} product={p} />)}
                </div>
                {newProducts[3] && (
                  <div className="col-span-1">
                    <ProductCard product={newProducts[3]} />
                  </div>
                )}
              </div>
              <div className="text-center mt-8 md:mt-12">
                <Link to="/new-arrivals" className="inline-flex items-center gap-2 px-8 py-3 text-xs tracking-[0.3em] uppercase transition-all duration-300 hover:scale-105 hover:shadow-lg" style={{ backgroundColor: `hsl(${theme.btnBg})`, color: `hsl(${theme.btnText})`, borderRadius: theme.btnRadius, fontFamily: "var(--design-font-ui)", fontWeight: 700 }}>
                  View All <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
          ) : (
            <div className="container mx-auto px-4" style={{ maxWidth: 1440 }}>
              <div className="text-center mb-8 md:mb-14">
                <div className="h-3 w-24 mx-auto mb-3 rounded" style={{ backgroundColor: `hsl(${theme.mutedText} / 0.15)` }} />
                <div className="h-8 w-48 mx-auto rounded" style={{ backgroundColor: `hsl(${theme.cardText} / 0.1)` }} />
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="rounded-lg overflow-hidden" style={{ aspectRatio: "3/4", backgroundColor: `hsl(${theme.cardText} / 0.08)` }} />
                ))}
              </div>
            </div>
          )}
        </section>
      </LazySection>

      <LazySection height="36px" rootMargin="0px">
        <div data-section-id="marquee" className="py-2 overflow-hidden flex items-center" style={{ backgroundColor: `hsl(${theme.cardAccent})`, height: "36px" }}>
          <div className="animate-marquee whitespace-nowrap flex items-center gap-12" style={{ animationDuration: "45s" }}>
            {Array.from({ length: 2 }).map((_, i) => (
              <span key={i} className="text-[10px] md:text-xs tracking-[0.3em] md:tracking-[0.5em] uppercase leading-none" style={{ color: `hsl(${theme.btnText})`, fontFamily: "var(--design-font-ui)", fontWeight: 700 }}>
                {siteT("marquee_ticker_text", "✦ Premium Fashion ✦ New Season ✦ Handcrafted")}
              </span>
            ))}
          </div>
        </div>
      </LazySection>

      <LazySection height="200px" rootMargin="0px">
        <DynamicBanner placement="top" />
      </LazySection>

      <LazySection height="600px" rootMargin="0px" onVisible={() => setBestVisible(true)}>
        <BestSellingSection enabled={bestVisible} />
      </LazySection>

      <LazySection height="700px" rootMargin="0px" onVisible={() => setAllVisible(true)}>
        <AllCollectionsSection enabled={allVisible} />
      </LazySection>

      <LazySection height="200px" rootMargin="0px">
        <DynamicBanner placement="middle" />
      </LazySection>

      <LazySection height="200px" rootMargin="0px">
        <DynamicBanner placement="bottom" />
      </LazySection>

      <LazySection height="100px" rootMargin="0px">
        <Suspense fallback={null}>
          <FeaturesBanner />
        </Suspense>
      </LazySection>
    </>
  );
};

export default Design1;
